import DOMPurify from 'dompurify';

export default defineNuxtPlugin(({ vueApp }) => {
  vueApp.directive('changeable', {
    beforeMount(el, binding) {
      el._changeableBinding = binding;
      el._changeableHandler = ({ target: { value } }) => {
        const [model, key] = el._changeableBinding.value;
        if (!model || !key) {
          return;
        }
        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        if (!model.$changes) {
          model.$changes = {};
        }
        model.$changes[key] = trimmedValue;
      };
      el.addEventListener('input', el._changeableHandler);
    },
    updated(el, binding) {
      el._changeableBinding = binding;
    },
    unmounted(el, binding) {
      const [model, key] = binding.value;
      if (model?.$changes) {
        const { [key]: _, ...rest } = model.$changes;
        model.$changes = rest;
      }
      el.removeEventListener('input', el._changeableHandler);
      el._changeableBinding = undefined;
      el._changeableHandler = undefined;
    },
  });

  vueApp.directive('truncate', (el, binding) => {
    const maxWidth =
      typeof binding.value === 'number'
        ? `${binding.value}px`
        : (binding.value ?? '150px');

    el.style.maxWidth = maxWidth;
    el.style.minWidth = '0';
    el.style.overflow = 'hidden';
    el.style.whiteSpace = 'nowrap';
    el.style.textOverflow = 'ellipsis';
    el.style.display = 'block';
  });

  vueApp.directive('hover-expand', {
    mounted(el) {
      const findTextElement = () => {
        return (
          el.querySelector('[data-hover-expand-text]') ||
          el.querySelector('span, h5, p') ||
          el
        );
      };

      const textElement = findTextElement();

      const originalStyles = {
        overflow: textElement.style.overflow,
        textOverflow: textElement.style.textOverflow,
        whiteSpace: textElement.style.whiteSpace,
        wordBreak: textElement.style.wordBreak,
        position: textElement.style.position,
        zIndex: textElement.style.zIndex,
      };

      el._hoverExpandTextElement = textElement;
      el._hoverExpandOriginalStyles = originalStyles;

      const isOverflowing = () =>
        textElement.scrollWidth > textElement.clientWidth;

      const applyDefaultStyles = () => {
        textElement.style.overflow = 'hidden';
        textElement.style.textOverflow = 'ellipsis';
        textElement.style.whiteSpace = 'nowrap';

        textElement.style.wordBreak = originalStyles.wordBreak;
        textElement.style.position = originalStyles.position;
        textElement.style.zIndex = originalStyles.zIndex;
      };

      const handleExpand = () => {
        if (!isOverflowing()) return;

        textElement.style.overflow = 'visible';
        textElement.style.whiteSpace = 'normal';
        textElement.style.wordBreak = 'break-word';
        textElement.style.position = 'relative';
        textElement.style.zIndex = '10';
      };

      const handleReset = () => applyDefaultStyles();

      applyDefaultStyles();

      el._hoverExpandEnter = handleExpand;
      el._hoverExpandLeave = handleReset;

      el.addEventListener('mouseenter', handleExpand);
      el.addEventListener('mouseleave', handleReset);

      el.addEventListener('focusin', handleExpand);
      el.addEventListener('focusout', handleReset);
    },

    unmounted(el) {
      el.removeEventListener('mouseenter', el._hoverExpandEnter);
      el.removeEventListener('mouseleave', el._hoverExpandLeave);
      el.removeEventListener('focusin', el._hoverExpandEnter);
      el.removeEventListener('focusout', el._hoverExpandLeave);

      const textElement = el._hoverExpandTextElement;
      const original = el._hoverExpandOriginalStyles;

      if (textElement && original) {
        textElement.style.overflow = original.overflow;
        textElement.style.textOverflow = original.textOverflow;
        textElement.style.whiteSpace = original.whiteSpace;
        textElement.style.wordBreak = original.wordBreak;
        textElement.style.position = original.position;
        textElement.style.zIndex = original.zIndex;
      }

      delete el._hoverExpandEnter;
      delete el._hoverExpandLeave;
      delete el._hoverExpandTextElement;
      delete el._hoverExpandOriginalStyles;
    },
  });

  vueApp.directive('scroll', {
    mounted(el, binding) {
      window.addEventListener('scroll', binding.value);
    },
    unmounted(el, binding) {
      window.removeEventListener('scroll', binding.value);
    },
  });

  vueApp.directive('scroll-style', {
    mounted(el, binding) {
      const threshold = binding.arg ? parseInt(binding.arg) : 100;

      const handler = (e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;

        // Emit scrollTop directly — gives full flexibility
        binding.value(scrollTop > threshold, scrollTop);
      };

      el.addEventListener('scroll', handler);
      el._scrollStyleHandler = handler;
    },
    unmounted(el) {
      el.removeEventListener('scroll', el._scrollStyleHandler);
      delete el._scrollStyleHandler;
    },
  });

  // State storage for infinite-scroll observer
  const infiniteScrollObserverState = new WeakMap();

  vueApp.directive('infinite-scroll', {
    mounted(el, binding) {
      let offset = 50;

      if (Array.isArray(binding.arg)) {
        offset = parseInt(binding.arg[0]) || offset;
      } else if (typeof binding.arg === 'number') {
        offset = binding.arg;
      } else if (typeof binding.arg === 'string') {
        offset = parseInt(binding.arg) || offset;
      }

      let loading = false;

      // Try to find the scrollable child div. this is Kendo UI specific.
      const scrollTarget =
        el.querySelector('div.k-listview-content[role="list"]') || el;

      // Create a sentinel element at the end of the scroll container
      // we'll look for sentinel to trigger the infinite scroll, it's a bit more reliable
      const sentinel = document.createElement('div');
      sentinel.style.height = `${offset}px`;
      sentinel.style.width = '100%';
      sentinel.className = 'infinite-scroll-sentinel';
      scrollTarget.appendChild(sentinel);

      const observer = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !loading &&
            scrollTarget.children.length > 1 && // make sure there's more childner here
            scrollTarget.scrollHeight > scrollTarget.clientHeight // make sure it runs if it's actually scrollable
          ) {
            loading = true;
            (async () => {
              try {
                await binding.value();
              } finally {
                loading = false;
              }
            })().catch((err) => {
              console.error('Infinite scroll handler error:', err);
            });
          }
        },
        {
          root: scrollTarget,
          rootMargin: `0px 0px ${offset}px 0px`,
          threshold: 0.1,
        }
      );

      observer.observe(sentinel);
      infiniteScrollObserverState.set(el, { observer, sentinel, scrollTarget });
    },

    unmounted(el) {
      const state = infiniteScrollObserverState.get(el);
      if (state) {
        state.observer.disconnect();
        if (state.scrollTarget.contains(state.sentinel))
          state.scrollTarget.removeChild(state.sentinel);
        infiniteScrollObserverState.delete(el);
      }
    },
  });

  vueApp.directive('safe-html', (el, binding) => {
    const content = binding.value;

    // Check if the content originates from the whitelisted domain
    if (content) {
      if (isUrlFromWhitelist(content)) {
        el.innerHTML = content;
        return;
      }
    }

    // If not whitelisted, sanitize using DOMPurify
    const sanitizedHtml = DOMPurify.sanitize(content);

    // Create a temporary container to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = sanitizedHtml;

    // Find all links and set target="_blank"
    const links = tempDiv.querySelectorAll('a');
    links.forEach((link) => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });

    // Replace the element's innerHTML with the modified content
    el.innerHTML = tempDiv.innerHTML;
  });

  vueApp.directive('click-outside', {
    mounted(el, binding) {
      const handler = (event) => {
        if (!el.contains(event.target)) {
          if (typeof binding.value === 'function') {
            binding.value(event, binding.arg);
          }
        }
      };

      el._clickOutsideHandler = handler;

      document.addEventListener('click', handler, { capture: true });
    },

    beforeUnmount(el) {
      document.removeEventListener('click', el._clickOutsideHandler, {
        capture: true,
      });
      delete el._clickOutsideHandler;
    },
  });
});

// Helper function to check if URL is from the whitelist
function isUrlFromWhitelist(content) {
  const whitelistUrl = 'https://emsere-dev.odoo.com'; // Replace with your actual whitelist URL
  const urlRegex = /https?:\/\/[^\s"]+/;

  // Extract the URL from the content
  const extractedUrl = content?.match(urlRegex)?.[0];

  return extractedUrl && new URL(extractedUrl).origin === whitelistUrl;
}
